import React from 'react';
import { ExternalLink, ArrowRight, Atom, Layers, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
import { mentisPlatform } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Featured platform section for Smartlab subsidiary — showcases MENTIS.
 * Coherence instrument with 3-step protocol, verdict types, and Quantum Mode.
 */
const MENTISSection = ({ accentColor = '#4A90D9' }) => {
  const { pick } = useLanguage();
  const stepIcons = [Layers, Atom, Activity];

  return (
    <section
      className="section-padding-lg border-t relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0b0e14 0%, #0e1218 100%)',
        borderColor: `${accentColor}33`
      }}
    >
      {/* Ambient blue glow */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div
          className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full blur-3xl"
          style={{ background: `radial-gradient(circle, ${accentColor}40 0%, transparent 70%)` }}
        />
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-3xl"
          style={{ background: `radial-gradient(circle, ${accentColor}30 0%, transparent 70%)` }}
        />
      </div>

      <div className="container-swg relative z-10">
        {/* Eyebrow + title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <span
            className="inline-block px-4 py-1.5 rounded-full text-[11px] tracking-[0.2em] uppercase mb-6 font-mono"
            style={{ color: accentColor, background: `${accentColor}15`, border: `1px solid ${accentColor}40` }}
            data-testid="mentis-eyebrow"
          >
            {pick(mentisPlatform.eyebrow)}
          </span>

          <div className="flex items-center justify-center gap-4 mb-4 flex-wrap">
            <img
              src={mentisPlatform.logo}
              alt="MENTIS"
              className="h-16 w-16 object-contain drop-shadow-[0_0_20px_rgba(74,144,217,0.5)]"
              data-testid="mentis-logo"
            />
            <h2 className="text-5xl md:text-6xl font-serif tracking-wide text-white">MENTIS</h2>
          </div>
          <p className="text-2xl md:text-3xl font-serif italic mt-2" style={{ color: accentColor }}>
            "{pick(mentisPlatform.tagline)}"
          </p>
          <p className="text-[#9AACB8] text-base mt-4 font-mono tracking-[0.15em] uppercase">
            {pick(mentisPlatform.pullQuote)}
          </p>
        </motion.div>

        {/* Preview + brief */}
        <div className="grid lg:grid-cols-5 gap-12 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3"
          >
            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="block relative group rounded-2xl overflow-hidden border shadow-[0_20px_60px_-20px_rgba(74,144,217,0.4)]"
              style={{ borderColor: `${accentColor}40` }}
              data-testid="mentis-preview-link"
            >
              <img
                src={mentisPlatform.preview}
                alt="MENTIS preview"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                loading="lazy"
              />
              <div className="absolute bottom-4 right-4 px-4 py-2 rounded-full backdrop-blur-md text-white text-xs font-medium flex items-center gap-2 opacity-90 group-hover:opacity-100 transition-opacity" style={{
                background: `${accentColor}30`,
                border: `1px solid ${accentColor}80`
              }}>
                <ExternalLink className="w-3.5 h-3.5" />
                Live preview
              </div>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <p className="text-lg text-white leading-relaxed mb-6">{pick(mentisPlatform.shortBrief)}</p>

            {/* Coherence signature */}
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg mb-8 font-mono text-sm"
                 style={{ background: `${accentColor}10`, border: `1px solid ${accentColor}40`, color: accentColor }}>
              <span className="text-[10px] uppercase tracking-widest opacity-70">Signature</span>
              <span className="font-bold">{mentisPlatform.signature}</span>
            </div>

            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold tracking-wide uppercase text-sm transition-all duration-300 shadow-[0_8px_30px_rgba(74,144,217,0.3)] hover:shadow-[0_12px_40px_rgba(74,144,217,0.5)] hover:scale-[1.03]"
              style={{ background: accentColor, color: '#0b0e14' }}
              data-testid="mentis-cta"
            >
              {pick(mentisPlatform.cta)}
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>

        {/* 3-step protocol */}
        <div className="mb-20">
          <h3 className="text-center text-[#9AACB8] uppercase tracking-[0.3em] text-xs mb-10 font-mono">
            The 3-step protocol
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {mentisPlatform.protocol.map((step, index) => {
              const Icon = stepIcons[index];
              return (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="swg-card rounded-2xl p-8 relative overflow-hidden border"
                  style={{ borderColor: `${accentColor}30` }}
                  data-testid={`mentis-step-${step.step}`}
                >
                  <span
                    className="absolute top-4 right-6 text-7xl font-serif opacity-10"
                    style={{ color: accentColor }}
                  >
                    {step.step}
                  </span>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 relative z-10"
                    style={{ background: `${accentColor}20`, border: `1px solid ${accentColor}40` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: accentColor }} />
                  </div>
                  <h4 className="text-white font-serif text-2xl mb-2 relative z-10">{pick(step.name)}</h4>
                  <p className="text-[#9AACB8] text-sm leading-relaxed relative z-10">{pick(step.desc)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Verdict labels strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-3 mb-20"
          data-testid="mentis-verdicts"
        >
          <span className="text-[#9AACB8] uppercase tracking-[0.2em] text-xs mr-4 font-mono">
            Verdicts
          </span>
          {mentisPlatform.verdicts.map((v) => (
            <span
              key={v}
              className="px-4 py-2 rounded-full text-xs font-mono tracking-[0.15em] uppercase font-semibold"
              style={{
                color: accentColor,
                background: `${accentColor}10`,
                border: `1px solid ${accentColor}40`
              }}
            >
              {v}
            </span>
          ))}
        </motion.div>

        {/* Quantum Mode card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto rounded-2xl p-10 relative overflow-hidden border"
          style={{
            background: `linear-gradient(135deg, ${accentColor}15 0%, transparent 100%)`,
            borderColor: `${accentColor}40`
          }}
          data-testid="mentis-quantum"
        >
          <div className="flex items-start gap-5">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${accentColor}25`, border: `1px solid ${accentColor}60` }}
            >
              <Atom className="w-7 h-7" style={{ color: accentColor }} />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-serif text-2xl mb-3">
                {pick(mentisPlatform.quantumMode.title)}
              </h3>
              <p className="text-[#9AACB8] leading-relaxed mb-5">
                {pick(mentisPlatform.quantumMode.desc)}
              </p>
              <div className="flex flex-wrap gap-2">
                {pick(mentisPlatform.features).map((feature) => (
                  <span
                    key={feature}
                    className="text-xs px-3 py-1 rounded-full text-[#9AACB8] border"
                    style={{ borderColor: `${accentColor}30` }}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MENTISSection;
